import { IRelatedDocumentSummary } from '@query/documentRelations'
import {
  autoUpdate,
  flip,
  FloatingPortal,
  offset,
  safePolygon,
  shift,
  size,
  useClick,
  useDismiss,
  useFloating,
  useHover,
  useInteractions
} from '@floating-ui/react'
import { AnimatePresence, motion } from 'framer-motion'
import React, { Suspense, useRef, useState } from 'react'
import styled, { css } from 'styled-components'

const DocumentMiniDetail = React.lazy(() =>
  import('./DocumentMiniDetail').then(module => ({ default: module.DocumentMiniDetail }))
)

interface Props {
  documentId: number
  summary?: IRelatedDocumentSummary | null
  children: React.ReactNode
  disabled?: boolean
  /** Shrink-wrap the trigger, for use inside inline text such as a voucher number. */
  inline?: boolean
}

const VIEWPORT_PADDING = 12

export const DocumentRelationPreview: React.FC<Props> = ({
  documentId,
  summary,
  children,
  disabled = false,
  inline = false
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const portalRootRef = useRef<HTMLElement | null>(document.getElementById('dropdown-root'))

  const { context, refs, x, y, strategy } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement: 'right-start',
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(VIEWPORT_PADDING),
      flip({
        padding: VIEWPORT_PADDING,
        fallbackPlacements: ['left-start', 'bottom-start', 'top-start', 'bottom-end', 'top-end']
      }),
      shift({ padding: VIEWPORT_PADDING, crossAxis: true }),
      size({
        padding: VIEWPORT_PADDING,
        apply({ availableWidth, availableHeight, elements }) {
          if (!elements.floating) return
          Object.assign(elements.floating.style, {
            maxWidth: `${Math.min(availableWidth, 400)}px`,
            maxHeight: `${Math.min(availableHeight, 460)}px`
          })
        }
      })
    ]
  })

  const dismiss = useDismiss(context, { outsidePress: true })
  const click = useClick(context)
  const hover = useHover(context, {
    delay: { open: 450, close: 150 },
    handleClose: safePolygon()
  })

  const { getReferenceProps, getFloatingProps } = useInteractions([click, hover, dismiss])

  if (disabled) {
    return <>{children}</>
  }

  return (
    <>
      <PreviewTrigger ref={refs.setReference} $inline={inline} {...getReferenceProps()}>
        {children}
      </PreviewTrigger>
      <FloatingPortal root={portalRootRef}>
        <AnimatePresence>
          {isOpen ? (
            <PreviewPanel
              ref={refs.setFloating}
              style={{
                top: y ?? 0,
                left: x ?? 0,
                position: strategy
              }}
              {...getFloatingProps()}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
            >
              <Suspense fallback={null}>
                <DocumentMiniDetail
                  documentId={documentId}
                  summary={summary}
                  isOpen={isOpen}
                  onNavigate={() => setIsOpen(false)}
                />
              </Suspense>
            </PreviewPanel>
          ) : null}
        </AnimatePresence>
      </FloatingPortal>
    </>
  )
}

const PreviewTrigger = styled.div<{ $inline?: boolean }>`
  display: ${({ $inline }) => ($inline ? 'inline-flex' : 'flex')};
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  min-width: 0;
  cursor: default;
  ${({ $inline }) =>
    !$inline &&
    css`
      flex: 1;
    `}
`

const PreviewPanel = styled(motion.div)`
  z-index: 10000;
  display: flex;
  flex-direction: column;
  width: min(400px, calc(100vw - ${VIEWPORT_PADDING * 2}px));
  max-height: inherit;
  background: ${({ theme }) => theme.colors.neutralWhite};
  border: 1px solid ${({ theme }) => theme.colors.neutralGray};
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  box-shadow: ${({ theme }) => theme.shadow.md};
  overflow: hidden;
`
